import React from "react";

/**
 * QenvaWordmark — brand-kit v2 lockup.
 *
 * Two visual paths, chosen by tone:
 *
 *   - "light" → composite (transparent shield PNG + "QENVA" wordmark rendered
 *               as HTML text in Bodoni Moda serif). Used on light backgrounds
 *               where the Deep-Navy shield of `v2-icon-only.png` contrasts.
 *
 *   - "dark"  → SHIELD-ONLY image `/brand/v2-dark-shield.png`, meant for
 *               compact/narrow contexts (Sidebar, mobile top bar) where the
 *               full lockup with tagline would be illegible in miniature.
 *
 *               The shield asset is the exact bbox-detected crop of the
 *               official "Dark Background" artwork (ratio 608/782 ≈ 0.777),
 *               resized proportionally via `height={size} + width: auto +
 *               object-fit: contain`. Never stretched, never distorted.
 *
 * For large surfaces (Header, Footer, Pricing header) render the *full*
 * lockup `/brand/v2-dark-lockup.png` directly via a plain <img>. This
 * component is intentionally not the vehicle for that use-case.
 */
const ICON_SRC_LIGHT = "/brand/v2-icon-only.png";
const SHIELD_SRC_DARK = "/brand/v2-dark-shield.png";

export default function QenvaWordmark({
  tone = "light",
  size = 32,
  className = "",
  alt = "QENVA · The Silent Guardian",
  showIcon = true,
  testId,
}) {
  // --- Dark tone: shield-only image, proportional resize, no crop, no stretch.
  if (tone === "dark") {
    return (
      <span
        className={`inline-flex items-center ${className}`}
        aria-label={alt}
        data-testid={testId || "qenva-wordmark-dark"}
      >
        <img
          src={SHIELD_SRC_DARK}
          alt={alt}
          height={size}
          draggable={false}
          style={{
            height: size,
            width: "auto",
            objectFit: "contain",
            display: "block",
            userSelect: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            boxShadow: "none",
          }}
          data-testid="qenva-wordmark-icon"
        />
      </span>
    );
  }

  // --- Light tone: composite (icon + HTML wordmark text in Bodoni Moda). ---
  const iconHeight = size;
  const iconWidth = size; // v2-icon-only.png is a 1024×1024 square
  const wordmarkFontSize = Math.round(size * 0.6);
  return (
    <span
      className={`inline-flex items-center ${showIcon ? "gap-2" : ""} ${className}`}
      aria-label={alt}
      data-testid={testId || "qenva-wordmark-light"}
    >
      {showIcon && (
        <img
          src={ICON_SRC_LIGHT}
          width={iconWidth}
          height={iconHeight}
          alt=""
          draggable={false}
          style={{
            height: iconHeight,
            width: iconWidth,
            display: "block",
            userSelect: "none",
            border: "none",
            outline: "none",
            background: "transparent",
            boxShadow: "none",
          }}
          data-testid="qenva-wordmark-icon"
        />
      )}
      <span
        style={{
          fontFamily: "'Bodoni Moda', 'Playfair Display', Georgia, serif",
          fontWeight: 500,
          fontSize: `${wordmarkFontSize}px`,
          letterSpacing: "0.02em",
          lineHeight: 1,
          color: "#0B2341",
          transform: "translateY(0.5px)",
        }}
        data-testid="qenva-wordmark-text"
      >
        QENVA
      </span>
    </span>
  );
}
